<template>
  <div class="manifest-search-results elevated">
    <SkeletonDataTable
      :key="dataTableKey"
      table-class="p-datatable-sm"
      :value="results.data?.content"
      :loading="results.loading"
      :number-of-rows="5"
      :first="results.data?.pageable?.offset ?? 0"
      :rows="manifestSearchCriteria.pageSize"
      :total-records="results.data?.totalElements"
      :sort-field="manifestSearchCriteria.sortField"
      :sort-order="manifestSearchCriteria.sortOrder"
      :rows-per-page-options="[10, 20, 50]"
      paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      data-key="id"
      responsive-layout="scroll"
      current-page-report-template="Showing {first} to {last} of {totalRecords}"
      auto-layout
      lazy
      paginator
      reorderable-columns
      scrollable
      scroll-height="flex"
      @column-reorder="onColumnReorder"
      @page="emit('page', $event)"
      @remove="onTableColumnRemove"
      @sort="emit('sort', $event)"
    >
      <template #empty>
        {{
          t('common.noParamMatchingSearch', {
            param: t('manifests.manifests', 2),
          })
        }}
      </template>

      <RygenColumn
        key="id"
        field="id"
        :header="t('common.id')"
        :reorderable-column="false"
        :removable-sort="false"
        sortable
        frozen
      >
        <template #body="{ data }">
          <router-link
            :to="{
              name: 'manifestDetail',
              params: { manifestId: data.id },
              query: { search: searchParams },
            }"
          >
            {{ data.id }}
          </router-link>
        </template>
      </RygenColumn>

      <RygenColumn
        v-for="col of internalColumns"
        :key="col.key"
        :field="col.field"
        :header="col.header"
        :class="col.class"
        :sortable="col.sortable"
        removable
      >
        <template v-if="col.body" #body="{ data, index }">
          <component
            :is="col.body"
            :data="data"
            :index="index"
            :search="searchParams"
            type="MANIFEST"
          />
        </template>
      </RygenColumn>

      <ColumnSelectActionHeader
        key="columnSelectAction"
        v-model:visible="isDrawerVisible"
      />

      <template #paginatorstart></template>
      <template #paginatorend>
        <RygenButton
          mode="secondary"
          icon="pi pi-refresh"
          :aria-label="t('common.refresh')"
          @click="emit('refresh-no-cache')"
        />
      </template>
    </SkeletonDataTable>

    <ColumnSelectDrawer
      v-model:visible="isDrawerVisible"
      :columns="internalColumns"
      :options="columnOptions"
      @add="onColumnAdd"
      @add-all="onColumnsAdd"
      @column-reorder="onColumnReorderDrawer"
      @remove="onColumnRemove"
      @remove-all="onColumnsRemove"
    />
  </div>
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDataTableColumns } from '@rygen/rygen-ui/composables';
import {
  ColumnSpecification,
  Loadable,
  PrimeVueReorderEvent,
} from '@rygen/rygen-ui/model';
import { stringify } from '@rygen/rygen-ui/util';

import {
  MANIFEST_COLUMNS,
  ManifestSearchCriteria,
  ManifestSearchResult,
} from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import ColumnSelectDrawer from '@rygen/rygen-ui/components/search/ColumnSelectDrawer.vue';
import ColumnSelectActionHeader from '@rygen/rygen-ui/components/ui/ColumnSelectActionHeader.vue';
import RygenColumn from '@rygen/rygen-ui/components/ui/RygenColumn.vue';
import SkeletonDataTable from '@rygen/rygen-ui/components/ui/SkeletonDataTable.vue';
import { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable';

// One non-reorderable leading column (the frozen manifest-number link) precedes the
// ColumnSpecification loop.
const NON_REORDERABLE_COLUMNS_OFFSET = 1;

const props = defineProps({
  manifestSearchCriteria: {
    type: ManifestSearchCriteria,
    required: true,
  },
  results: {
    type: Object as PropType<Loadable<ManifestSearchResult>>,
    default: () => ({ loading: false, data: new ManifestSearchResult() }),
  },
  columns: {
    type: Array as PropType<ColumnSpecification[]>,
    default: () => MANIFEST_COLUMNS,
  },
  columnOptions: {
    type: Array as PropType<ColumnSpecification[]>,
    default: () => MANIFEST_COLUMNS,
  },
});
const { manifestSearchCriteria, results, columns } = toRefs(props);
const emit = defineEmits<{
  (event: 'edited-columns', newColumns: ColumnSpecification[]): void;
  (event: 'page', payload: DataTablePageEvent): void;
  (event: 'refresh-no-cache'): void;
  (event: 'sort', payload: DataTableSortEvent): void;
  (event: 'update:columns', newVal: ColumnSpecification[]): void;
}>();

const { t } = useI18n();

const internalColumns: Ref<ColumnSpecification[]> = ref(columns.value);
const {
  addColumn,
  addColumns,
  dataTableKey,
  removeColumn,
  removeColumns,
  reorderColumn,
} = useDataTableColumns(internalColumns);

const isDrawerVisible = ref(false);

const searchParams = computed(() => stringify(manifestSearchCriteria.value));

const onTableColumnRemove = (colField: string) => {
  const column = internalColumns.value.find((col) => col.field === colField);
  if (column) {
    onColumnRemove(column);
  }
};

const onColumnReorder = (event: PrimeVueReorderEvent) =>
  onColumnReorderDrawer(
    {
      ...event,
      dragIndex: event.dragIndex - NON_REORDERABLE_COLUMNS_OFFSET,
      dropIndex: event.dropIndex - NON_REORDERABLE_COLUMNS_OFFSET,
    },
    true,
  );

const onColumnAdd = (column: ColumnSpecification) => {
  addColumn(column);
  emit('edited-columns', internalColumns.value);
};

const onColumnsAdd = (cols: ColumnSpecification[]) => {
  addColumns(cols);
  emit('edited-columns', internalColumns.value);
};

const onColumnRemove = (column: ColumnSpecification) => {
  removeColumn(column);
  emit('edited-columns', internalColumns.value);
};

const onColumnsRemove = (cols: ColumnSpecification[]) => {
  removeColumns(cols);
  emit('edited-columns', internalColumns.value);
};

const onColumnReorderDrawer = (
  event: PrimeVueReorderEvent,
  isTableUpdate = false,
) => {
  reorderColumn(event, isTableUpdate);
  emit('edited-columns', internalColumns.value);
};

watch(
  columns,
  (newVal: ColumnSpecification[]) => {
    internalColumns.value = newVal;
  },
  { deep: true, immediate: true },
);

watch(internalColumns, (newVal: ColumnSpecification[]) => {
  emit('update:columns', newVal);
});
</script>

<style>
.manifest-search-results {
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .data-table-container {
    max-width: none;
  }

  .manifest-date--today,
  .manifest-date--tomorrow {
    font-weight: var(--font-weight-bold);

    .manifest-date__badge {
      margin-left: var(--sp-1);
      padding: 0 var(--sp-1);
      border-radius: var(--border-radius);
      font-size: 0.75rem;
      font-weight: var(--font-weight-normal);
    }
  }

  .manifest-date--today .manifest-date__badge {
    background: var(--flash-info-light, #e3f2fd);
    color: var(--flash-info, #1565c0);
  }

  .manifest-date--tomorrow .manifest-date__badge {
    background: var(--surface-200, #eceff1);
    color: var(--text-color-secondary, #546e7a);
  }
}
</style>
